import isMobileAppRuntime, { getMobileAppPlatform } from 'utils/isMobileAppRuntime';
import { getMobilePushContext } from 'utils/mobileNativeBridge';
import { registerMobileFamilyPushDevice, registerMobilePushDevice, unregisterMobilePushDevice } from './api';

const DEVICE_ID_KEY = 'flcMobilePushDeviceId';
const FAMILY_ACCOUNT_TOKEN_KEY = 'flcMobileFamilyAccountToken';
let started = false;
let syncPromise = null;

const safeStorageGet = (key) => {
  try { return window.localStorage.getItem(key) || ''; } catch (error) { return ''; }
};

const safeStorageSet = (key, value) => {
  try { window.localStorage.setItem(key, value); } catch (error) { /* no-op */ }
};

const createFallbackDeviceId = () => {
  const existing = safeStorageGet(DEVICE_ID_KEY);
  if (existing) return existing;
  const value = `flc-${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  safeStorageSet(DEVICE_ID_KEY, value);
  return value;
};

const getNativeDeviceId = async () => {
  const plugin = window.Capacitor?.Plugins?.Device;
  if (plugin?.getId) {
    try {
      const result = await plugin.getId();
      const value = String(result?.identifier || '').trim();
      if (value) return value;
    } catch (error) {
      console.warn('[mobile-push][device-id-failed]', error);
    }
  }
  return createFallbackDeviceId();
};

export const syncMobilePushDevice = async () => {
  const jwtToken = safeStorageGet('jwtToken');
  const familyAccountToken = safeStorageGet(FAMILY_ACCOUNT_TOKEN_KEY);
  if (!isMobileAppRuntime() || (!jwtToken && !familyAccountToken)) return { skipped: true };
  if (syncPromise) return syncPromise;

  syncPromise = (async () => {
    const context = await getMobilePushContext({ waitForRegistrationMs: 3000 });
    const platform = getMobileAppPlatform();
    if (!context.token || !['ios', 'android'].includes(platform)) {
      return { skipped: true, reason: context.permission || 'token-unavailable' };
    }
    const deviceId = await getNativeDeviceId();
    const values = {
      token: context.token,
      permission: context.permission,
      platform,
      deviceId,
    };
    if (jwtToken) return registerMobilePushDevice(values);
    return registerMobileFamilyPushDevice({ ...values, familyAccountToken });
  })();

  try {
    return await syncPromise;
  } catch (error) {
    console.warn('[mobile-push][device-sync-failed]', error);
    return { skipped: true, reason: 'request-failed' };
  } finally {
    syncPromise = null;
  }
};

export const unregisterCurrentMobilePushDevice = async () => {
  if (!isMobileAppRuntime() || !safeStorageGet('jwtToken')) return { skipped: true };
  try {
    const context = await getMobilePushContext({ waitForRegistrationMs: 0 });
    const deviceId = await getNativeDeviceId();
    return await unregisterMobilePushDevice({ token: context.token || '', deviceId });
  } catch (error) {
    console.warn('[mobile-push][device-unregister-failed]', error);
    return { skipped: true, reason: 'request-failed' };
  }
};

export const startMobilePushModule = () => {
  if (!isMobileAppRuntime() || typeof window === 'undefined' || started) return;
  started = true;
  window.addEventListener('flc:push-registration', syncMobilePushDevice);
  const appPlugin = window.Capacitor?.Plugins?.App;
  if (typeof appPlugin?.addListener === 'function') {
    appPlugin.addListener('appStateChange', ({ isActive }) => {
      if (isActive) syncMobilePushDevice();
    });
  }
  syncMobilePushDevice();
};
